import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import DashboardLayout from '../../components/DashboardLayout';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Image,
  MoveUp,
  MoveDown
} from 'lucide-react';

interface User {
  email: string;
}

interface HeroSlide {
  id: string;
  title: string;
  subtitle: string;
  image_url: string | null;
  button_text: string;
  button_link: string;
  order: number;
  active: boolean;
}

interface SlideFormData {
  id?: string;
  title: string;
  subtitle: string;
  image?: File | null;
  image_url?: string;
  button_text: string;
  button_link: string;
  order: number;
  active: boolean;
}

interface PageProps {
  auth: {
    user: User;
  };
  hero_slides: HeroSlide[];
  store_logo?: string;
  store_name?: string;
}

const HeroSlides = ({ hero_slides: initialSlides, store_logo, store_name }: PageProps) => {
  const [slides, setSlides] = useState(initialSlides || []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [newSlide, setNewSlide] = useState<any>({
    title: '',
    subtitle: '',
    image: null,
    button_text: '',
    button_link: '',
    order: slides.length + 1,
    active: true
  });

  const handleToggleActive = async (id: string) => {
    try {
      const response = await fetch(`/admin/hero_slides/${id}/toggle_active`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });
      
      if (response.ok) {
        setSlides(prev => prev.map(slide => 
          slide.id === id ? { ...slide, active: !slide.active } : slide
        ));
      }
    } catch (error) {
      console.error('Error toggling slide status:', error);
    }
  };

  const handleDeleteSlide = async (id: string) => {
    if (confirm('¿Estás seguro de que quieres eliminar este slide?')) {
      try {
        const response = await fetch(`/admin/hero_slides/${id}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          }
        });
        
        if (response.ok) {
          setSlides(prev => prev.filter(slide => slide.id !== id));
        }
      } catch (error) {
        console.error('Error deleting slide:', error);
      }
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>, isEditing = false) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      if (isEditing) {
        setEditingSlide(prev => ({ ...prev, image: file } as SlideFormData));
      } else {
        setNewSlide(prev => ({ ...prev, image: file } as SlideFormData));
      }
    }
  };

  const handleMoveSlide = (id: string, direction: 'up' | 'down') => {
    const currentIndex = slides.findIndex(slide => slide.id === id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex >= 0 && newIndex < slides.length) {
      const newSlides = [...slides];
      [newSlides[currentIndex], newSlides[newIndex]] = [newSlides[newIndex], newSlides[currentIndex]];
      
      // Actualizar el orden
      newSlides.forEach((slide, index) => {
        slide.order = index + 1;
      });
      
      setSlides(newSlides);
    }
  };

  const handleSaveSlide = async () => {
    try {
      const formData = new FormData();
      const slideData = editingSlide || newSlide;
      
      // Add CSRF token to form data for Rails
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (csrfToken) {
        formData.append('authenticity_token', csrfToken);
      }

      formData.append('hero_slide[title]', slideData.title);
      formData.append('hero_slide[subtitle]', slideData.subtitle);
      formData.append('hero_slide[button_text]', slideData.button_text);
      formData.append('hero_slide[button_link]', slideData.button_link);
      formData.append('hero_slide[order]', String(slideData.order));
      formData.append('hero_slide[active]', String(slideData.active));

      if (slideData.image) {
        formData.append('hero_slide[image]', slideData.image);
      }

      const url = editingSlide ? `/admin/hero_slides/${editingSlide.id}` : '/admin/hero_slides';
      const method = editingSlide ? 'PATCH' : 'POST';

       const response = await fetch(url, {
         method: method,
         body: formData,
         headers: {
           'Accept': 'application/json',
           'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
         }
       });

       const data = await response.json();

       if (response.ok) {
         if (editingSlide) {
           // Update the slide in state
           setSlides(prev => prev.map(s => s.id === editingSlide.id ? data : s));
         } else {
           // Add the new slide to state
           setSlides(prev => [...prev, data]);
         }
         setIsModalOpen(false);
         if (previewUrl) {
           URL.revokeObjectURL(previewUrl);
           setPreviewUrl(null);
         }
       } else {
         alert(data.errors.join(', '));
       }
    } catch (error) {
      console.error('Error saving slide:', error);
    }
  };

  const handleEditSlide = (slide: HeroSlide) => {
    setEditingSlide({ ...slide });
    setIsModalOpen(true);
  };

  const handleNewSlide = () => {
    setEditingSlide(null);
    setNewSlide({
      title: '',
      subtitle: '',
      image: null,
      button_text: '',
      button_link: '',
      order: slides.length + 1,
      active: true
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSlide(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setNewSlide({
      title: '',
      subtitle: '',
      image: null,
      button_text: '',
      button_link: '',
      order: slides.length + 1,
      active: true
    });
  };

  return (
    <DashboardLayout title="Slides del Hero" storeLogo={store_logo} storeName={store_name}>
      <div className="relative mb-8 p-8 rounded-2xl bg-gradient-to-r from-slate-400 via-slate-500 to-slate-600 shadow-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-300/20 via-slate-400/20 to-slate-500/20 animate-pulse" />
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-xl" />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Slides del Hero</h1>
            <p className="text-slate-50 text-lg">Gestiona las imágenes promocionales de tu tienda</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30">
              <Eye className="h-4 w-4 mr-2" />
              Vista Previa
            </Button>
            <Button onClick={handleNewSlide} className="bg-white text-slate-700 hover:bg-slate-50 shadow-lg hover:shadow-xl transition-all duration-200">
              <Plus className="h-5 w-5 mr-2" />
              Nuevo Slide
            </Button>
          </div>
        </div>
      </div>
        {/* Slides List */}
        <div className="space-y-6">
          {slides.map((slide, index) => (
            <Card key={slide.id} className="relative overflow-hidden border-0 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                index % 3 === 0 ? 'from-slate-500/5 via-slate-600/5 to-slate-700/5' :
                index % 3 === 1 ? 'from-gray-500/5 via-slate-500/5 to-slate-600/5' :
                'from-blue-500/5 via-slate-500/5 to-slate-600/5'
              }`} />
              <CardHeader className="relative z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-col items-center space-y-1 p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveSlide(slide.id, 'up')}
                        disabled={index === 0}
                        className="hover:bg-slate-200 dark:hover:bg-slate-600"
                      >
                        <MoveUp className="h-4 w-4" />
                      </Button>
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{slide.order}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveSlide(slide.id, 'down')}
                        disabled={index === slides.length - 1}
                        className="hover:bg-slate-200 dark:hover:bg-slate-600"
                      >
                        <MoveDown className="h-4 w-4" />
                      </Button>
                    </div>
                    <div>
                      <CardTitle className="flex items-center space-x-3 text-lg">
                        <div className="p-2 rounded-lg bg-gradient-to-r from-slate-600 to-slate-700 shadow-sm">
                          <Image className="h-5 w-5 text-white" />
                        </div>
                        <span className="font-bold text-slate-800 dark:text-slate-200">{slide.title}</span>
                        <Badge variant={slide.active ? "default" : "secondary"} className={slide.active ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white" : ""}>
                          {slide.active ? "Activo" : "Inactivo"}
                        </Badge>
                      </CardTitle>
                      <CardDescription className="text-base text-slate-600 dark:text-slate-400 mt-1">{slide.subtitle}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleActive(slide.id)}
                      className="hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-indigo-300 dark:border-indigo-600 text-indigo-600 dark:text-indigo-400 hover:border-indigo-400 dark:hover:border-indigo-500 transition-all duration-200"
                    >
                      {slide.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditSlide(slide)}
                      className="hover:bg-blue-50 dark:hover:bg-blue-900/20 border-blue-300 dark:border-blue-600 text-blue-600 dark:text-blue-400 hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-200"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteSlide(slide.id)}
                      className="hover:bg-red-50 dark:hover:bg-red-900/20 border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 hover:border-red-400 dark:hover:border-red-500 transition-all duration-200"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      {slide.image_url ? (
                        <img
                          src={slide.image_url}
                          alt={slide.title}
                          className="w-full h-48 object-cover rounded-xl shadow-sm hover:shadow-md transition-shadow"
                        />
                      ) : (
                        <div className="w-full h-48 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 rounded-xl flex items-center justify-center">
                          <Image className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                        </div>
                      )}
                    </div>
                   <div className="space-y-4">
                     <div>
                       <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Título</Label>
                       <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">{slide.title}</p>
                     </div>
                     <div>
                       <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Subtítulo</Label>
                       <p className="text-sm text-slate-600 dark:text-slate-400">{slide.subtitle}</p>
                     </div>
                     <div>
                       <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Botón</Label>
                       <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                         {slide.button_text} → {slide.button_link}
                       </p>
                     </div>
                   </div>
                 </div>
               </CardContent>
             </Card>
           ))}
         </div>

        {/* Modal para crear/editar slide */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 border-0 shadow-2xl">
            <DialogHeader className="pb-6">
              <DialogTitle className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                {editingSlide ? 'Editar Slide' : 'Nuevo Slide'}
              </DialogTitle>
              <DialogDescription className="text-base text-slate-600 dark:text-slate-400">
                {editingSlide ? 'Modifica los datos del slide' : 'Agrega un nuevo slide al hero'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Título</Label>
                    <Input
                      id="title"
                      value={editingSlide?.title || newSlide.title}
                      onChange={(e) => {
                        if (editingSlide) {
                          setEditingSlide(prev => ({ ...prev, title: e.target.value }));
                        } else {
                          setNewSlide(prev => ({ ...prev, title: e.target.value }));
                        }
                      }}
                      placeholder="Título del slide"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subtitle">Subtítulo</Label>
                    <Input
                      id="subtitle"
                      value={editingSlide?.subtitle || newSlide.subtitle}
                      onChange={(e) => {
                        if (editingSlide) {
                          setEditingSlide(prev => ({ ...prev, subtitle: e.target.value }));
                        } else {
                          setNewSlide(prev => ({ ...prev, subtitle: e.target.value }));
                        }
                      }}
                      placeholder="Subtítulo del slide"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="image">Imagen</Label>
                    <div className="space-y-2">
                      <Input
                        id="image"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, !!editingSlide)}
                        className="cursor-pointer"
                      />
                      {(editingSlide?.image || newSlide.image) && (
                        <div className="text-sm text-green-600 flex items-center">
                          <Image className="h-4 w-4 mr-1" />
                          Archivo seleccionado: {(editingSlide?.image || newSlide.image).name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="button_text">Texto del Botón</Label>
                    <Input
                      id="button_text"
                      value={editingSlide?.button_text || newSlide.button_text}
                      onChange={(e) => {
                        if (editingSlide) {
                          setEditingSlide(prev => ({ ...prev, button_text: e.target.value }));
                        } else {
                          setNewSlide(prev => ({ ...prev, button_text: e.target.value }));
                        }
                      }}
                      placeholder="Ver Productos"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="button_link">Enlace del Botón</Label>
                    <Input
                      id="button_link"
                      value={editingSlide?.button_link || newSlide.button_link}
                      onChange={(e) => {
                        if (editingSlide) {
                          setEditingSlide(prev => ({ ...prev, button_link: e.target.value }));
                        } else {
                          setNewSlide(prev => ({ ...prev, button_link: e.target.value }));
                        }
                      }}
                      placeholder="/products"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="order">Orden</Label>
                    <Input
                      id="order"
                      type="number"
                      value={editingSlide?.order || newSlide.order}
                      onChange={(e) => {
                        if (editingSlide) {
                          setEditingSlide(prev => ({ ...prev, order: parseInt(e.target.value) }));
                        } else {
                          setNewSlide(prev => ({ ...prev, order: parseInt(e.target.value) }));
                        }
                      }}
                      placeholder="1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Preview */}
              <div className="space-y-2">
                <Label>Vista Previa</Label>
                <div className="relative h-48 rounded-lg overflow-hidden border">
                  {previewUrl || editingSlide?.image_url ? (
                    <img
                      key={previewUrl || editingSlide?.image_url}
                      src={previewUrl || editingSlide?.image_url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <Image className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-black">
                      <h3 className="text-xl font-bold">
                        {editingSlide?.title || newSlide.title || 'Título del Slide'}
                      </h3>
                      <p className="text-sm mt-2">
                        {editingSlide?.subtitle || newSlide.subtitle || 'Subtítulo del slide'}
                      </p>
                      {(editingSlide?.button_text || newSlide.button_text) && (
                        <Button className="mt-4" variant="secondary">
                          {editingSlide?.button_text || newSlide.button_text}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="pt-6 border-t border-slate-200 dark:border-slate-700">
              <Button variant="outline" onClick={handleCloseModal} className="hover:bg-slate-50 dark:hover:bg-slate-800 border-slate-300 dark:border-slate-600">
                Cancelar
              </Button>
              <Button onClick={handleSaveSlide} className="bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white shadow-lg hover:shadow-xl transition-all duration-200">
                <Plus className="h-4 w-4 mr-2" />
                {editingSlide ? 'Actualizar Slide' : 'Crear Slide'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </DashboardLayout>
  );
};

export default HeroSlides;